import { Alert, Box, Button, Chip, FormControlLabel, Grid, MenuItem, Paper, Switch, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import InfoButton from '../../../../../Components/InfoButton';
import FieldLabel from '../../../../../Components/FieldLabel';
import { FEATURE_DESCRIPTION } from '../../../constant';
import { COMPOSITION_ID_OPTIONS, LANGUAGE_OPTIONS } from './constants';
import { renderValue } from './helpers';
import useStyles from './feedConfigurationCardStyles';
import RssFillIcon from '../../../../../Components/CustomIcons/rssIcon';

const { RSS_INFO_MESSAGES } = FEATURE_DESCRIPTION.VIDEO_CONTENT;

const getStatusChipProps = (value) => {
	return String(value).toLowerCase() === 'active'
		? { label: 'Active', color: 'success' }
		: { label: 'Inactive', color: 'error' };
};

const getStatusCardClassName = (value, classes) => {
	return String(value).toLowerCase() === 'active' ? classes.statusCardActive : classes.statusCardInactive;
};

const getPollingScheduleHelperText = (pollIntervalMinutes) => {
	if (!isPositiveSafeInteger(pollIntervalMinutes)) {
		return 'Enter a positive whole number of minutes.';
	}

	const hours = pollIntervalMinutes / 60;
	return `Equivalent to ${hours.toLocaleString(undefined, { maximumFractionDigits: 2 })} hour${hours === 1 ? '' : 's'}.`;
};

const isPositiveSafeInteger = (value) => Number.isSafeInteger(value) && value > 0;

const FeedConfigurationCard = ({
	feedForm,
	feed,
	hasExistingFeed,
	isFeatureEnabled,
	isExperimentalUser,
	loading,
	feedSummary,
	onChange,
	onSave,
	onPoll,
	onDelete
}) => {
	const classes = useStyles();
	const isFeedUnavailable = loading.feed || feed === false;
	const areFeedSettingsDisabled = !isFeatureEnabled || isFeedUnavailable || !feedForm.active;
	const feedStatus = feedSummary.find((item) => item.label === 'Status')?.value;
	const statusCardClassName = feedStatus ? getStatusCardClassName(feedStatus, classes) : '';

	return (
		<Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
			<Typography variant="h6" fontWeight={700}>
				Feed Configuration
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
				Register one RSS feed for this site and control its polling schedule.
			</Typography>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}>
						<FormControlLabel
							sx={{ m: 0 }}
							control={
								<Switch
									checked={feedForm.active}
									onChange={(e) => onChange('active', e.target.checked)}
									disabled={!isFeatureEnabled || isFeedUnavailable}
								/>
							}
							label={
								<Typography component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
									Enable Feed
									<InfoButton
										message={RSS_INFO_MESSAGES.FEED_ACTIVE}
										size="small"
										iconSize="small"
										customIconStyles={{ padding: 0.25 }}
									/>
								</Typography>
							}
						/>
						{feedStatus && (
							<Paper
								variant="outlined"
								className={statusCardClassName}
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 1,
									px: 1.5,
									py: 1.75,
									borderRadius: 2
									
								}}
							>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 700 }}
								>
									<RssFillIcon width={20} height={20} />
									RSS Feed Service
								</Typography>
								<Chip {...getStatusChipProps(feedStatus)} size="small" sx={{ fontWeight: 700, minWidth: 70 }} />
							</Paper>
						)}
					</Box>
				</Grid>
				<Grid item xs={12}>
					<TextField
						fullWidth
						label="Feed URL"
						value={feedForm.feedUrl}
						onChange={(e) => onChange('feedUrl', e.target.value)}
						disabled={areFeedSettingsDisabled}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<FieldLabel infoMessage={RSS_INFO_MESSAGES.DAILY_LIMIT}>
						Daily Limit
					</FieldLabel>
					<TextField
						fullWidth
						type="number"
						value={feedForm.dailyLimit}
						onChange={(e) => onChange('dailyLimit', e.target.value === '' ? '' : e.target.valueAsNumber)}
						disabled={areFeedSettingsDisabled}
						inputProps={{ min: 1, step: 1 }}
						error={!isPositiveSafeInteger(feedForm.dailyLimit)}
						helperText={
							isPositiveSafeInteger(feedForm.dailyLimit) ? '' : 'Enter a positive whole number.'
						}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<FieldLabel infoMessage={RSS_INFO_MESSAGES.LANGUAGE}>
						Language
					</FieldLabel>
					<TextField
						fullWidth
						select
						value={feedForm.language}
						onChange={(e) => onChange('language', e.target.value)}
						disabled={areFeedSettingsDisabled}
					>
						{LANGUAGE_OPTIONS.map((language) => (
							<MenuItem key={language.value} value={language.value}>
								{language.label}
							</MenuItem>
						))}
					</TextField>
				</Grid>
				<Grid item xs={12} sm={6}>
					<FieldLabel infoMessage={RSS_INFO_MESSAGES.POLLING_SCHEDULE}>
						Sync Interval
					</FieldLabel>
					<TextField
						fullWidth
						type="number"
						value={feedForm.pollIntervalMinutes}
						onChange={(e) =>
							onChange('pollIntervalMinutes', e.target.value === '' ? '' : e.target.valueAsNumber)
						}
						disabled={areFeedSettingsDisabled}
						inputProps={{ min: 1, step: 1 }}
						helperText={getPollingScheduleHelperText(feedForm.pollIntervalMinutes)}
					/>
				</Grid>
				{isExperimentalUser && (
					<Grid item xs={12} sm={6}>
						<FieldLabel infoMessage={RSS_INFO_MESSAGES.VIDEO_TEMPLATE}>
							Video Template
						</FieldLabel>
						<TextField
							fullWidth
							select
							value={feedForm.compositionId}
							onChange={(e) => onChange('compositionId', e.target.value)}
							disabled={areFeedSettingsDisabled}
						>
							{COMPOSITION_ID_OPTIONS.map((composition) => (
								<MenuItem key={composition.value} value={composition.value}>
									{composition.label}
								</MenuItem>
							))}
						</TextField>
					</Grid>
				)}
				
			</Grid>

			<Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
				<Button
					variant="contained"
					onClick={onSave}
					loading={loading.saveFeed}
					startIcon={<SaveOutlinedIcon />}
					disabled={
						isFeedUnavailable ||
						!isFeatureEnabled ||
						!feedForm.feedUrl ||
						!isPositiveSafeInteger(feedForm.dailyLimit) ||
						!isPositiveSafeInteger(feedForm.pollIntervalMinutes)
					}
				>
					{hasExistingFeed ? 'Update Feed' : 'Create Feed'}
				</Button>
				<Button
					variant="outlined"
					onClick={onPoll}
					startIcon={<ReplayOutlinedIcon />}
					loading={loading.pollFeed}
					disabled={!hasExistingFeed || !isFeatureEnabled || isFeedUnavailable || !feedForm.active}
				>
					Sync Feed Now
				</Button>
				<Button
					variant="outlined"
					color="error"
					startIcon={<DeleteOutlineIcon />}
					onClick={onDelete}
					disabled={!hasExistingFeed || loading.deleteFeed || !isFeatureEnabled || isFeedUnavailable}
				>
					Delete Feed
				</Button>
			</Box>

			{loading.feed ? (
				<Typography sx={{ mt: 3 }} color="text.secondary">
					Loading feed details...
				</Typography>
			) : feed === false ? (
				<Alert severity="error" sx={{ mt: 3 }}>
					RSS feed service is unavailable. Feed controls are disabled until the service is reachable again.
				</Alert>
			) : (
				<Box sx={{ mt: 3 }}>
					{feedSummary.length === 0 && <Alert severity="info">No feed is registered for this site yet.</Alert>}
					{feedSummary.length > 0 && (
						<Grid container spacing={1.5}>
							{feedSummary.filter((item) => item.label !== 'Status').map((item) => {
								return (
									<Grid item xs={12} sm={6} key={item.label}>
										<Paper
											variant="outlined"
											sx={{
												p: 1.75,
												height: '100%',
												borderRadius: 2,
												backgroundColor: 'background.paper'
											}}
										>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.6 }}
											>
												{item.label}
											</Typography>
											<Typography variant="body1" fontWeight={700} sx={{ lineHeight: 1.35 }}>
												{renderValue(item.value)}
											</Typography>
										</Paper>
									</Grid>
								);
							})}
						</Grid>
					)}
				</Box>
			)}
		</Paper>
	);
};

export default FeedConfigurationCard;
